import cx from 'classnames';
import {find, isFunction, map, union} from 'lodash';
import {Children, useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import SelectField, {OnChangeValue, components} from 'react-select';

import {FormControl, RequiredMark} from 'components';
import {Option} from 'interfaces/option';
import {TRANSLATION_KEYS} from 'locale';

import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.COMMON.ALL;

const {ValueContainer, Placeholder, NoOptionsMessage} = components;

const CustomValueContainer = (props: any) => (
    <ValueContainer {...props}>
        <Placeholder {...props}>
            {props.selectProps.placeholder}
        </Placeholder>
        <div className='values'>
            {Children.map(props.children, (child) =>
                child && child.type !== Placeholder ? child : null
            )}
        </div>
    </ValueContainer>
);

const CustomNoOptionsMessage: React.FC<any> = (props: any) => {
    const {t} = useTranslation();
    return (
        <NoOptionsMessage {...props}>
            {t(T_KEYS.NO_RESULT)}
        </NoOptionsMessage>
    );
};

const IconOption: React.FC<any> = (props: any) => (
    <components.Option {...props}>
        {props.data.icon && <img className='option-icon' src={props.data.icon} alt={props.data.name} />}
        {isFunction(props.data.renderIcon) && props.data.renderIcon(props.data.id)}
        <div className='option-value'>{props.data.name}</div>
    </components.Option>
);

const SingleValue: React.FC<any> = (props: any) => {
    const hasIcon = props.data.icon || isFunction(props.data.renderIcon);
    return (
        <components.SingleValue {...props}>
            {props.data.icon && <img className='single-value-icon' src={props.data.icon} alt={props.data.name} />}
            {isFunction(props.data.renderIcon) && props.data.renderIcon(props.data.id)}
            <div className={cx('single-value-name', {'with-icon': hasIcon})}>{props.data.name}</div>
        </components.SingleValue>
    );
};

type Props = {
    value: any;
    hasError?: boolean;
    errorMessage?: string;
    options: Option<any, any>[];
    noneOption?: boolean;
    noneOptionLabel?: string;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    required?: boolean;
    multiple?: boolean;
    fill?: 'light' | 'transparent';
    hidePlaceholder?: boolean; // hide placeholder if the select has value
    isSearchable?: boolean;
    isClearable?: boolean;
    /**
     * works for multiple=false cases
     */
    valueAs?: 'option' | 'id';
    collapseEmptyOption?: boolean;
    filterOption?: (option: any) => boolean;
    onChange: (value: any) => void;
    onInputChange?: (value: string) => void;
};

export type SelectProps = Props;

export const Select: React.FC<Props> = ({
    value: valueProp,
    options: optionsProp,
    noneOption,
    noneOptionLabel,
    hasError,
    errorMessage,
    placeholder,
    className,
    disabled,
    required,
    hidePlaceholder,
    isSearchable = false,
    isClearable = false,
    fill = 'light',
    valueAs = 'option',
    collapseEmptyOption = true,
    filterOption,
    multiple = false,
    onChange,
    onInputChange
}: Props) => {
    const {t} = useTranslation();

    const [initialValue] = useState<string | number>(valueProp);

    const isDirty = useMemo<boolean>(() => valueProp !== initialValue, [initialValue, valueProp]);

    const options = useMemo(() => noneOption ? union([{id: null, name: noneOptionLabel || t(T_KEYS.NONE)}], optionsProp) : optionsProp,
        [t, noneOption, optionsProp, noneOptionLabel]);

    const customComponents = useMemo(() => ({
        Option: IconOption,
        SingleValue,
        IndicatorSeparator: () => null,
        ValueContainer: hidePlaceholder ? ValueContainer : CustomValueContainer,
        NoOptionsMessage: CustomNoOptionsMessage
    }), [hidePlaceholder]);

    const computedValue = useMemo(() => {
        if (multiple) {
            if (valueAs === 'option') {
                return valueProp;
            } else if (valueAs === 'id') {
                return map(valueProp, (id) => find(options, {id}));
            }
        }
        let value = null;
        if (valueAs === 'option') {
            value = valueProp;
        } else if (valueAs === 'id') {
            value = find(options, {id: valueProp});
        }
        return !collapseEmptyOption ? value : value?.id ? value : null;
    }, [multiple, valueAs, valueProp, options, collapseEmptyOption]);

    const handleChange = useCallback((newValue: OnChangeValue<Option, boolean>) => {
        if (newValue) {
            if (multiple) {
                newValue = newValue as Option[];
                onChange(valueAs === 'option' ? newValue : valueAs === 'id' ? map(newValue, 'id') : newValue);
            } else {
                newValue = newValue as Option;
                onChange(valueAs === 'option' ? newValue : valueAs === 'id' ? newValue.id : null);
            }
        } else {
            onChange(multiple ? [] : null);
        }
    }, [multiple, valueAs, onChange]);

    const styles = useMemo(() => {
        let result = {
            menuPortal: (provided) => ({
                ...provided,
                zIndex: 9999
            }),
            menu: (provided) => ({
                ...provided,
                zIndex: 9999
            })
        };

        if (!hidePlaceholder) {
            result = {
                ...result,
                container: (provided) => ({
                    ...provided,
                    marginTop: 16
                }),
                valueContainer: (provided) => ({
                    ...provided,
                    overflow: 'visible'
                }),
                placeholder: (provided, state) => ({
                    ...provided,
                    position: 'absolute',
                    top: (state.hasValue || state.selectProps.inputValue) ? -16 : 5,
                    transition: 'top 0.1s, font-size 0.1s',
                    fontSize: (state.hasValue || state.selectProps.inputValue) && 11
                })
            };
        }

        return result;
    }, [hidePlaceholder]);

    const renderPlaceholder = useCallback(() => (
        <span>
            {placeholder}
            {required && <RequiredMark className='tw-ml-1' />}
        </span>
    ), [required, placeholder]);

    return (
        <FormControl {...{
            value: computedValue, className: cx(className, 'vs-select', fill),
            required, disabled, hasError, errorMessage, dirty: isDirty
        }}
        >
            {() => (
                <SelectField
                    isMulti={multiple}
                    menuPortalTarget={document.body}
                    menuPlacement='auto'
                    className='control tw-w-full'
                    classNamePrefix='vs-select'
                    isDisabled={disabled}
                    placeholder={renderPlaceholder()}
                    options={options}
                    value={computedValue}
                    isClearable={isClearable}
                    isSearchable={isSearchable}
                    components={customComponents}
                    styles={styles}
                    getOptionLabel={(option) => option.name}
                    filterOption={filterOption}
                    getOptionValue={(option) => option.id}
                    onInputChange={onInputChange}
                    onChange={handleChange}
                />
            )}
        </FormControl>
    );
};
